
import React, { useState, useRef, useEffect } from 'react';

interface DateRange {
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
}

interface DateRangePickerProps {
  range: DateRange;
  onChange: (range: DateRange) => void;
  onClear: () => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({ range, onChange, onClear }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const containerRef = useRef<HTMLDivElement>(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDateLabel = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

  const handleDateClick = (dayStr: string) => {
    if (!range.from || (range.from && range.to)) {
      onChange({ from: dayStr, to: '' });
    } else {
      const fromDate = new Date(range.from);
      const toDate = new Date(dayStr);
      if (toDate < fromDate) {
        onChange({ from: dayStr, to: '' });
      } else {
        onChange({ ...range, to: dayStr });
        setIsOpen(false);
      }
    }
  };

  const isSelected = (dayStr: string) => dayStr === range.from || dayStr === range.to;
  const isInRange = (dayStr: string) => {
    if (!range.from || !range.to) return false;
    const d = new Date(dayStr);
    return d > new Date(range.from) && d < new Date(range.to);
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const totalDays = daysInMonth(year, month);
    const startDay = firstDayOfMonth(year, month);
    const days = [];

    // Empty cells for alignment
    for (let i = 0; i < startDay; i++) {
        days.push(<div key={`empty-${i}`} className="h-9 w-9" />);
    }

    for (let d = 1; d <= totalDays; d++) {
      const dayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const selected = isSelected(dayStr);
      const inRange = isInRange(dayStr);

      days.push(
        <div
          key={dayStr}
          onClick={() => handleDateClick(dayStr)}
          className={`h-9 w-9 flex items-center justify-center text-xs font-semibold cursor-pointer rounded-lg transition-all
            ${selected ? 'bg-[#136bce] text-white shadow-md' : 
              inRange ? 'bg-blue-50 text-[#136bce]' : 
              'text-slate-600 hover:bg-slate-100'}`}
        >
          {d}
        </div>
      );
    }

    return (
      <div className="p-4 bg-white rounded-xl shadow-2xl border border-slate-200 w-[280px]">
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={() => setCurrentMonth(new Date(year, month - 1))}
            className="p-1 hover:bg-slate-100 rounded-md text-slate-400 hover:text-slate-600 transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">chevron_left</span>
          </button>
          <span className="text-sm font-bold text-slate-900">
            {currentMonth.toLocaleDateString('default', { month: 'long', year: 'numeric' })}
          </span>
          <button 
            onClick={() => setCurrentMonth(new Date(year, month + 1))}
            className="p-1 hover:bg-slate-100 rounded-md text-slate-400 hover:text-slate-600 transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">chevron_right</span>
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <div key={day} className="h-9 w-9 flex items-center justify-center text-[10px] font-bold text-slate-400 uppercase tracking-wider">{day}</div>
          ))}
          {days}
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between">
           <button 
             onClick={onClear} 
             className="text-xs font-bold text-slate-400 hover:text-red-500 transition-colors uppercase tracking-wider"
           >
             Clear Selection
           </button>
           <button 
             onClick={() => setIsOpen(false)} 
             className="text-xs font-bold text-[#136bce] hover:underline uppercase tracking-wider"
           >
             Done
           </button>
        </div>
      </div>
    );
  };

  return (
    <div className="relative inline-block" ref={containerRef}>
      <div 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 cursor-pointer hover:bg-slate-50 transition-all shadow-sm min-w-[240px]"
      >
        <span className="material-symbols-outlined text-slate-400 text-[20px]">calendar_today</span>
        <span className="flex-1 font-medium text-slate-600">
          {range.from ? (
            <>
              {formatDateLabel(range.from)}
              {range.to ? ` - ${formatDateLabel(range.to)}` : ' - Select End'}
            </>
          ) : (
            'Select Date Range'
          )}
        </span>
        <span className={`material-symbols-outlined text-slate-400 text-[18px] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>expand_more</span>
      </div>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 z-[60] animate-in fade-in zoom-in-95 duration-200 origin-top-right">
          {renderCalendar()}
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
